import { yupResolver } from "@hookform/resolvers/yup";
import { message } from "antd";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  createCampaign,
  refreshTableCampaigns,
  selectCampaignForUpdate,
  selectCampaignsRefreshTable,
  selectIsUpdateCampaign,
  setCampaignForUpdate,
  setIsCampaignForUpdate,
  updateCampaign,
} from "../../../redux";
import { DataCampaigns } from "../../../redux/@types";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import { TableProperties } from "../../../shared/components/ui/Table/types";
import {
  validateCreateCampaigns,
  validateUpdateCampaigns,
} from "../../../helpers";

export const useCampaigns = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isUpdateCampaign = useSelector(selectIsUpdateCampaign);
  const dataUpdateCampaign = useSelector(selectCampaignForUpdate);
  const isRefreshTableCampaigns = useSelector(selectCampaignsRefreshTable);

  const [isLoading, setIsLoading] = useState(false);
  const [tableProps, setTableProps] = useState<TableProperties>();

  const navigateCampaignsCreate = () => {
    dispatch(setIsCampaignForUpdate(false));
    navigate(`/campaigns/new`);
  };

  const formMethodsCreate = useForm<DataCampaigns>({
    resolver: yupResolver(validateCreateCampaigns),
    defaultValues: {
      status: "inactive",
      products_mode: "manual",
      pill_bg_color: "#FE4914",
      pill_text_color: "#FFFFFF",
    },
  });

  const formMethodsUpdate = useForm<DataCampaigns>({
    resolver: yupResolver(validateUpdateCampaigns),
  });

  useEffect(() => {
    if (isUpdateCampaign) {
      formMethodsCreate.reset();
      formMethodsUpdate.reset({
        ...dataUpdateCampaign,
        category_id: dataUpdateCampaign?.category_id ?? undefined,
      });
    }

    return () => {
      formMethodsUpdate.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateCampaign, dataUpdateCampaign]);

  const updateStatus = async (value: "active" | "inactive", id: any) => {
    if (isLoading) return;
    setIsLoading(true);
    await updateCampaign({
      id,
      status: value,
    })
      .then((res: Partial<DataCampaigns>) => {
        setIsLoading(false);
        if (res.id) {
          message.success(
            `Se ha actualizado con éxito el estado de la campaña con id:${res.id}`
          );
          dispatch(refreshTableCampaigns(true));
        } else {
          message.error(res as string);
        }
      })
      .catch((err: any) => {
        setIsLoading(false);
        console.log(err);
        message.error(err.message);
      });
  };

  const onSubmitCreateOrUpdate = async (data: DataCampaigns) => {
    if (isLoading) return;
    setIsLoading(true);

    const payload: Partial<DataCampaigns> = {
      name: data.name,
      status: data.status,
      products_mode: data.products_mode,
      category_id:
        data.products_mode === "category"
          ? Number(data.category_id)
          : null,
      eyebrow: data.eyebrow ?? null,
      headline: data.headline ?? null,
      description: data.description ?? null,
      featured_title: data.featured_title ?? null,
      featured_description: data.featured_description ?? null,
      hero_image: data.hero_image ?? null,
      hero_image_mobile: data.hero_image_mobile ?? null,
      secondary_image: data.secondary_image ?? null,
      secondary_image_mobile: data.secondary_image_mobile ?? null,
      pill_label: data.pill_label ?? null,
      pill_bg_color: data.pill_bg_color ?? null,
      pill_text_color: data.pill_text_color ?? null,
    };

    if (isUpdateCampaign) {
      await updateCampaign({
        id: data.id,
        ...payload,
      })
        .then((res: Partial<DataCampaigns>) => {
          setIsLoading(false);
          if (res.id) {
            message.success(
              `Se ha actualizado con éxito la campaña con id:${res.id}`
            );
            dispatch(setCampaignForUpdate(res as DataCampaigns));
            dispatch(refreshTableCampaigns(true));
          } else {
            message.error(res as string);
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
          message.error(err.message);
        });
    } else {
      await createCampaign(payload)
        .then((res: DataCampaigns) => {
          setIsLoading(false);
          if (res.id) {
            message.success(
              `Se ha creado con éxito la campaña con id:${res.id}`
            );
            dispatch(refreshTableCampaigns(true));
            dispatch(setCampaignForUpdate(res));
            dispatch(setIsCampaignForUpdate(true));
            navigate(`/campaigns/${res.id}`, {
              replace: true,
              state: {
                campaignTab:
                  res.products_mode === "manual" ? "2" : "1",
              },
            });
            window.scrollTo(0, 0);
          } else {
            message.error(res as any);
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
          message.error(err.message);
        });
    }
  };

  useEffect(() => {
    if (isRefreshTableCampaigns) {
      tableProps?.refresh();
      dispatch(refreshTableCampaigns(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTableCampaigns]);

  return {
    isUpdateCampaign,
    isLoading,
    tableProps,
    formMethodsCreate,
    formMethodsUpdate,
    setTableProps,
    onSubmitCreateOrUpdate,
    navigateCampaignsCreate,
    updateStatus,
  };
};
